import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Cpu, FileKey, PackageCheck, Plus, RefreshCw } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { StatTiles } from '@/components/StatTiles';
import { StatusBadge } from '@/components/StatusBadge';
import { formatDate, formatDateTime } from '@/lib/utils';

interface DashboardProps {
  user: any;
}

interface DashboardLicense {
  id: string;
  key?: string;
  name?: string;
  status?: string;
  expiresAt?: number | null;
  createdAt?: number;
  activationCount?: number;
}

interface Overview {
  products: number;
  pendingActivations: number;
}

const EXPIRY_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

function Dashboard({ user }: DashboardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalLicenses: 0,
    activeLicenses: 0,
    expiredLicenses: 0,
    activations: 0,
  });
  const [recent, setRecent] = useState<DashboardLicense[]>([]);
  const [expiring, setExpiring] = useState<DashboardLicense[]>([]);
  const [overview, setOverview] = useState<Overview | null>(null);
  const [overviewFailed, setOverviewFailed] = useState(false);
  const [requestedAt, setRequestedAt] = useState<Date | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    loadDashboard().finally(() => setIsLoading(false));
  }, []);

  const loadDashboard = async () => {
    await Promise.all([fetchStats(), fetchOverview()]);
    setRequestedAt(new Date());
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await loadDashboard();
    setIsRefreshing(false);
  };

  const fetchOverview = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/dashboard/overview`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = response.ok ? await response.json() : null;
      const payload = data?.data;
      if (payload && !Array.isArray(payload) && typeof payload === 'object') {
        setOverview({
          products: Number(payload.products) || 0,
          pendingActivations: Number(payload.activations?.pending) || 0,
        });
        setOverviewFailed(false);
      } else {
        setOverview(null);
        setOverviewFailed(true);
      }
    } catch (error) {
      console.error('Failed to fetch overview:', error);
      setOverview(null);
      setOverviewFailed(true);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/licenses`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const licenses: any[] = data.data || [];

        const now = Date.now();
        const stats = {
          totalLicenses: licenses.length,
          activeLicenses: licenses.filter((l: any) => l.status === 'ACTIVE').length,
          expiredLicenses: licenses.filter(
            (l: any) => l.expiresAt && l.expiresAt < now
          ).length,
          activations: licenses.reduce(
            (sum: number, l: any) => sum + (l.activationCount || 0),
            0
          ),
        };

        setStats(stats);
        setRecent(
          [...licenses]
            .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
            .slice(0, 5)
        );
        setExpiring(
          licenses
            .filter((l) => l.status === 'ACTIVE' && l.expiresAt && l.expiresAt <= now + EXPIRY_WINDOW_MS)
            .sort((a, b) => (a.expiresAt || 0) - (b.expiresAt || 0))
        );
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="w-full space-y-6 px-4 py-6 sm:px-6 lg:px-8">
        <div className="space-y-6">
          <div className="space-y-2">
            <div className="h-8 w-48 animate-pulse rounded-md bg-muted" />
            <div className="h-5 w-72 max-w-full animate-pulse rounded-md bg-muted" />
          </div>
          <div className="grid grid-cols-[repeat(auto-fit,minmax(140px,1fr))] gap-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-24 animate-pulse rounded-md border bg-card" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const searchLink = (name?: string) => `/licenses?search=${encodeURIComponent(name || '')}`;

  return (
    <div className="w-full space-y-6 px-4 py-6 sm:px-6 lg:px-8">
      <PageHeader
        eyebrow={t('dashboard.eyebrow', { defaultValue: 'Workspace overview' })}
        title={t('dashboard.title')}
        subtitle={t('dashboard.welcome', { name: user?.name || user?.email })}
        actions={(
          <>
            <Button
              variant="outline"
              size="icon"
              aria-label={t('dashboard.refresh', { defaultValue: 'Refresh overview' })}
              onClick={handleRefresh}
              disabled={isRefreshing}
            >
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            </Button>
            <Button onClick={() => navigate('/licenses?create=true')}>
              <Plus className="mr-2 h-4 w-4" />
              New License
            </Button>
          </>
        )}
      />

      <StatTiles
        stats={[
          { label: t('dashboard.totalLicenses'), value: stats.totalLicenses },
          { label: t('dashboard.activeLicenses'), value: stats.activeLicenses, color: 'text-success' },
          { label: t('dashboard.expiredLicenses'), value: stats.expiredLicenses, color: 'text-destructive' },
          { label: t('dashboard.totalActivations'), value: stats.activations },
        ]}
      />

      <div className="dashboard-grid">
        <div className="od-stack" style={{ '--od-gap': '24px' } as React.CSSProperties}>
          <Card>
            <CardHeader>
              <div className="section-heading">
                <div>
                  <CardTitle className="text-lg">{t('dashboard.licenseActivity', { defaultValue: 'License activity' })}</CardTitle>
                  <CardDescription>{t('dashboard.licenseActivityDescription', { defaultValue: 'Recent licenses and activation events' })}</CardDescription>
                </div>
                <Button variant="ghost" onClick={() => navigate('/licenses')}>
                  View All Licenses
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {recent.length === 0 ? (
                <div className="empty-state">
                  <p>{t('dashboard.noActivity', { defaultValue: 'No license activity yet.' })}</p>
                </div>
              ) : (
                <div className="activity-list">
                  {recent.map((license) => (
                    <div key={license.id} className="activity-entry">
                      <div className="od-field">
                        <strong>{license.name || license.key}</strong>
                        <p>{t('dashboard.licenseCreated', { defaultValue: 'License created' })}</p>
                      </div>
                      <div className="od-field text-right">
                        <StatusBadge status={license.status} />
                        <time>{formatDateTime(license.createdAt)}</time>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">{t('dashboard.expiringSoon', { defaultValue: 'Expiring in the next 30 days' })}</CardTitle>
              <CardDescription>{t('dashboard.expiringSoonDescription', { defaultValue: 'Review upcoming renewals before access ends' })}</CardDescription>
            </CardHeader>
            <CardContent>
              {expiring.length === 0 ? (
                <div className="empty-state">
                  <p>{t('dashboard.noExpiring', { defaultValue: 'No licenses are due to expire in the next 30 days.' })}</p>
                </div>
              ) : (
                expiring.map((license) => (
                  <Link key={license.id} className="workflow-row" to={searchLink(license.name || license.key)}>
                    <div className="od-field od-fill">
                      <strong>{license.name || license.key}</strong>
                      <p>{license.key}</p>
                    </div>
                    <span className="text-sm od-nowrap">{formatDate(license.expiresAt)}</span>
                    <ArrowRight className="od-icon" />
                  </Link>
                ))
              )}
            </CardContent>
          </Card>
        </div>

        <div className="od-stack" style={{ '--od-gap': '24px' } as React.CSSProperties}>
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">{t('dashboard.licenseOperations', { defaultValue: 'License operations' })}</CardTitle>
              <CardDescription>{t('dashboard.licenseOperationsDescription', { defaultValue: 'From policy to an activated device' })}</CardDescription>
            </CardHeader>
            <CardContent>
              <WorkflowRow
                to="/licenses?create=true"
                icon={<FileKey className="od-icon" />}
                title="Create a license"
                description="Choose a policy and set activation limits"
              />
              <WorkflowRow
                to="/verification"
                icon={<PackageCheck className="od-icon" />}
                title="Prepare an offline package"
                description="Verify a license without a network connection"
              />
              <WorkflowRow
                to="/system-signatures"
                icon={<Cpu className="od-icon" />}
                title="Review hardware signatures"
                description="Approve or reject device bindings"
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">{t('dashboard.serviceConnection', { defaultValue: 'Service connection' })}</CardTitle>
              <CardDescription>{t('dashboard.serviceConnectionDescription', { defaultValue: 'Results from the last overview request' })}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="health-row">
                <span>Overview API</span>
                <HealthBadge ok={!overviewFailed} okLabel="AVAILABLE" failLabel="UNAVAILABLE" />
              </div>
              <div className="health-row">
                <span>Database</span>
                <HealthBadge ok={!overviewFailed} okLabel="CONNECTED" failLabel="UNKNOWN" />
              </div>
              {overview && (
                <>
                  <div className="health-row">
                    <span>Active products</span>
                    <strong className="font-mono">{overview.products}</strong>
                  </div>
                  <div className="health-row">
                    <span>Pending activations</span>
                    <strong className="font-mono">{overview.pendingActivations}</strong>
                  </div>
                </>
              )}
              <p className="health-caption">
                {requestedAt
                  ? `Last requested at ${requestedAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}. `
                  : ''}
                These checks do not measure uptime or offline verification health.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function WorkflowRow({
  to,
  icon,
  title,
  description,
}: {
  to: string;
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <Link to={to} className="workflow-row">
      {icon}
      <div className="od-field od-fill">
        <strong>{title}</strong>
        <p>{description}</p>
      </div>
      <ArrowRight className="od-icon" />
    </Link>
  );
}

function HealthBadge({ ok, okLabel, failLabel }: { ok: boolean; okLabel: string; failLabel: string }) {
  return (
    <Badge
      variant="outline"
      className={ok ? 'border-success/25 bg-success/10 text-success' : 'border-destructive/25 bg-destructive/10 text-destructive'}
    >
      {ok ? okLabel : failLabel}
    </Badge>
  );
}

export default Dashboard;
